import {
  Crosshair,
  Gauge,
  Move,
  Pause,
  Play,
  Radar,
  Radio,
  RotateCcw,
  Share2,
  Shield,
  Volume2,
  Wrench,
  createIcons,
} from "lucide";
import "./style.css";
import { RELEASE_VERSION } from "./core/config";
import type { GameEvent, PublicDiagnostics, SystemId } from "./core/types";
import { InputController } from "./input/controller";
import { AudioEngine } from "./platform/audio";
import { FrameMonitor } from "./platform/diagnostics";
import {
  mountPerformanceLab,
  parsePerformanceLabVersion,
} from "./performance-lab";
import { GameRenderer } from "./render/scene";
import { GameSimulation } from "./simulation/simulation";
import { HudController } from "./ui/hud";

declare global {
  interface Window {
    __ABYSS_RELAY__: PublicDiagnostics;
  }
}

function required<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing #${id}`);
  return element as T;
}

function parseSeed(): number {
  const value = Number(new URLSearchParams(window.location.search).get("seed"));
  if (Number.isFinite(value) && value > 0) return value >>> 0;
  const daily = new Date().toISOString().slice(0, 10).replaceAll("-", "");
  return Number(daily) || 731_993;
}

const parameters = new URLSearchParams(window.location.search);
const qaMode = parameters.get("qa") === "1";
const labVersion = parsePerformanceLabVersion(parameters);
if (!labVersion) {
  document.title = "Abyss Relay";
  document
    .querySelector<HTMLMetaElement>('meta[name="description"]')
    ?.setAttribute(
      "content",
      "Survive a three-minute procedural submarine run in Abyss Relay.",
    );
}
const seed = parseSeed();
const simulation = new GameSimulation(seed);
const renderer = new GameRenderer(required<HTMLCanvasElement>("game-canvas"), seed, {
  projectileMode: labVersion ?? "final",
});
const audio = new AudioEngine();
const frames = new FrameMonitor();
const eventCounts: Record<string, number> = {};
const errors: string[] = [];
let input: InputController;

if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("./sw.js").catch((error: unknown) => {
      errors.push(`Service worker: ${String(error)}`);
    });
  });
}

function recordEvents(events: GameEvent[]): void {
  for (const event of events) {
    eventCounts[event.type] = (eventCounts[event.type] ?? 0) + 1;
  }
}

function routePower(system: SystemId): void {
  simulation.routePower(system);
}

function togglePause(): void {
  const phase = simulation.snapshot().phase;
  if (phase === "playing") simulation.pause();
  else if (phase === "paused") simulation.resume();
}

function startRun(): void {
  simulation.start();
  void audio.start();
}

function restartRun(): void {
  simulation.restart();
  void audio.start();
}

const hud = new HudController({
  onStart: startRun,
  onPause: togglePause,
  onResume: () => simulation.resume(),
  onRestart: restartRun,
  onRoutePower: routePower,
  onSonar: () => input.queueSonar(),
  onAudioToggle: () => audio.toggle(),
});

input = new InputController(
  required<HTMLElement>("mobile-stick"),
  required<HTMLElement>("mobile-stick-knob"),
  {
    onPause: togglePause,
    onRoutePower: routePower,
  },
);

createIcons({
  icons: {
    Crosshair,
    Gauge,
    Move,
    Pause,
    Play,
    Radar,
    Radio,
    RotateCcw,
    Share2,
    Shield,
    Volume2,
    Wrench,
  },
});
hud.updateAudio(audio.isEnabled());

window.addEventListener("error", (event) => {
  errors.push(event.message || "Unknown runtime error");
});
window.addEventListener("unhandledrejection", (event) => {
  errors.push(String(event.reason));
});
document.addEventListener("visibilitychange", () => {
  if (document.hidden && simulation.snapshot().phase === "playing") simulation.pause();
});

function flushFrame(): void {
  const events = simulation.consumeEvents();
  recordEvents(events);
  const snapshot = simulation.frameView();
  audio.handle(events);
  renderer.update(snapshot, events);
  hud.update(snapshot);
  renderer.render();
}

let previous = performance.now();
function frame(now: number): void {
  frames.tick(now);
  const delta = Math.min(0.1, (now - previous) / 1_000);
  previous = now;
  simulation.advanceWallTime(delta, input.read());
  flushFrame();
  requestAnimationFrame(frame);
}

const diagnostics: PublicDiagnostics = {
  version: RELEASE_VERSION,
  seed,
  snapshot: () => simulation.snapshot(),
  performance: () => frames.snapshot(),
  rendering: () => renderer.diagnostics(),
  eventCounts: () => ({ ...eventCounts }),
  errors: () => [...errors],
};

if (labVersion) {
  diagnostics.lab = {
    version: labVersion,
    promptType: "reconstructed",
  };
}

if (qaMode) {
  diagnostics.vfx = () => renderer.vfxDiagnostics();
  diagnostics.start = () => {
    simulation.start();
    flushFrame();
  };
  diagnostics.advance = (seconds: number) => {
    if (simulation.snapshot().phase === "menu") simulation.start();
    simulation.advanceFixed(Math.max(0, Math.min(seconds, 240)));
    flushFrame();
  };
  diagnostics.scenario = (name: "combat" | "barrage" | "boss" | "won" | "lost") => {
    simulation.setQaScenario(name);
    flushFrame();
  };
  diagnostics.resetPerformance = () => frames.reset();
}

window.__ABYSS_RELAY__ = Object.freeze(diagnostics);

const requestedScenario = parameters.get("scenario");
if (labVersion) {
  simulation.setQaScenario("barrage");
  mountPerformanceLab({
    version: labVersion,
    performance: () => frames.snapshot(),
    rendering: () => renderer.diagnostics(),
    projectileCount: () => simulation.snapshot().projectiles.length,
  });
} else if (
  qaMode &&
  (requestedScenario === "combat" ||
    requestedScenario === "barrage" ||
    requestedScenario === "boss" ||
    requestedScenario === "won" ||
    requestedScenario === "lost")
) {
  diagnostics.scenario?.(requestedScenario);
}

flushFrame();
requestAnimationFrame(frame);
